import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "components/common/Breadcrumbs";
import theme from "lib/theme";
import { useSupportArticles, type SupportArticle } from "../../hooks/support/useSupportArticles";

export default function Support() {
  const navigate = useNavigate();
  const { articles, isLoading, isError } = useSupportArticles();

  // Group articles by category and then by topic
  const articlesByCategory = articles.reduce(
    (acc: Record<string, Record<string, SupportArticle[]>>, article: SupportArticle) => {
      if (!acc[article.category]) {
        acc[article.category] = {};
      }

      // Use the first tag as the topic, or "General" if no tags
      const topic = article.tags.length > 0 ? article.tags[0] : "General";

      if (!acc[article.category][topic]) {
        acc[article.category][topic] = [];
      }

      acc[article.category][topic].push(article);
      return acc;
    },
    {}
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load support articles. Please try again later or contact support directly.
        </Alert>
      </Box>
    );
  }

  return (
    <div>
      <Box>
        <Breadcrumbs data={[{ label: "Dashboard", to: "/" }, { label: "Support" }]} />
      </Box>

      <Typography variant="h4" mt={3} fontWeight="bold">
        Support Center
      </Typography>

      <Typography variant="body1" mt={1} mb={4}>
        Find answers to frequently asked questions and helpful guides
      </Typography>

      {articles.length === 0 ? (
        <Box>
          <ArticleIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No support articles available
          </Typography>
        </Box>
      ) : (
        <Stack direction="row" spacing={4} sx={{ alignItems: "flex-start" }}>
          {Object.entries(articlesByCategory).map(([category, topicsData]) => (
            <Box key={category} sx={{ flex: 1, minWidth: "400px" }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {category}
                </Typography>
                <Typography variant="body2" gutterBottom mb={2}>
                  {category === "General"
                    ? "Scheduling pickups, managing assets, viewing documents, donating your earnings."
                    : "Includes managing your profile, company, users and locations."}
                </Typography>
              </Box>

              {Object.entries(topicsData).map(([topic, topicArticles]) => (
                <Accordion
                  key={`${category}-${topic}`}
                  sx={{
                    mb: 1,
                    p: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    boxShadow: "none",
                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" component="h3">
                      {topic}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      {topicArticles.map((article: SupportArticle) => (
                        <Card
                          key={article.id}
                          sx={{
                            cursor: "pointer",
                            transition: "transform 0.2s",
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            boxShadow: "none",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "none",
                            },
                          }}
                          onClick={() => {
                            navigate(`/support/article/${article.id}`);
                          }}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                              <ArticleIcon color="primary" />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" component="h4" gutterBottom>
                                  {article.title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {article.content || "No content available"}
                                </Typography>
                                {article.tags.length > 1 && (
                                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    {article.tags.slice(1, 3).map((tag: string) => (
                                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                                    ))}
                                    {article.tags.length > 3 && (
                                      <Chip
                                        label={`+${article.tags.length - 3} more`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ))}
        </Stack>
      )}
    </div>
  );
}
